/**
 * @fileoverview The pending targets card of the Sales Performance screen.
 * @description Loads the open targets for the selected period and team and shows them with their
 * own loading and error states. The count line says how many are listed and how many of them are
 * overdue.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import PerformanceCard from '@/features/sales/components/PerformanceCard'
import PerformancePendingTable from '@/features/sales/components/PerformancePendingTable'
import TableSkeleton from '@/features/sales/components/TableSkeleton'
import { useSalesPerformancePending } from '@/features/sales/hooks/useSalesPerformance'

import type { SalesPerformanceQuery } from '@/features/sales/types/sales-performance.types'

interface PerformancePendingCardProps {
  query: SalesPerformanceQuery
}

/**
 * @component
 * @param {PerformancePendingCardProps} props - The selection.
 * @param {SalesPerformanceQuery} props.query - The selected period and team.
 */
function PerformancePendingCard({ query }: PerformancePendingCardProps) {
  const { t } = useTranslation()
  const pending = useSalesPerformancePending(query)

  return (
    <PerformanceCard
      title={t('sales.salesPerformance.pending.title')}
      testId="sales-performance-pending"
      busy={pending.isPlaceholderData}
      {...(pending.data && {
        meta: t('sales.salesPerformance.pending.meta', {
          count: pending.data.total,
          overdue: pending.data.overdueCount,
        }),
      })}
    >
      {pending.isPending && <TableSkeleton columns={4} rows={4} />}
      {pending.isError && (
        <p
          role="alert"
          className="text-compact text-danger"
          data-testid="sales-performance-pending-error"
        >
          {pending.error.message}
        </p>
      )}
      {pending.data && <PerformancePendingTable pending={pending.data} />}
      {pending.data && pending.data.total > pending.data.items.length && (
        <p className="text-xs text-slate" data-testid="sales-performance-pending-cap-note">
          {t('sales.salesPerformance.pending.capNote', {
            shown: pending.data.items.length,
            total: pending.data.total,
          })}
        </p>
      )}
    </PerformanceCard>
  )
}

export default PerformancePendingCard
